import React, { useState } from "react";
import logo from "../../assets/images/Logo.png";
import { Typography, IconButton, Menu, MenuItem } from "@mui/material";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ReactComponent as UserIcon } from "../../assets/images/userIcon.svg";
import { ReactComponent as HomeIcon } from "../../assets/images/homeIcon.svg";
import { ReactComponent as Home } from "../../assets/images/Home.svg";
import { ReactComponent as PartRequest } from "../../assets/images/PartRequest.svg";
import { ReactComponent as ArrowIcon } from "../../assets/images/ArrowIcon.svg";
import { ReactComponent as MatIcon } from "../../assets/images/MatIcon.svg";
import { ReactComponent as AddUserIcon } from "../../assets/images/AddNewUserIcon.svg";
import { ReactComponent as PartHistoryIcon } from "../../assets/images/PartHistory.svg";
import AddIcon from "@mui/icons-material/Add";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import { useMsal } from "@azure/msal-react";
import { automationHttpRequest } from "../../api/httpRequests";
import { toast } from "react-toastify";
import ReworkRequestsDialog from "./ReworkRequestsDialog";
import OwnerPartCreationDialog from "../ownerflow/OwnerPartCreationDialog";

import "../../styles/header.css";

const Header = ({ handleHideInsights }) => {
  const { instance } = useMsal();
  const [anchorEl, setAnchorEl] = useState(null);
  const [checkingRework, setCheckingRework] = useState(false);
  const [reworkDialogOpen, setReworkDialogOpen] = useState(false);
  const [reworkRequests, setReworkRequests] = useState([]);
  const [partRequestNumber, setPartRequestNumber] = useState();
  const [partCreationDialogOpen, setPartCreationDialogOpen] = useState(false);
  const [partCreationDetails, setPartCreationDetails] = useState();
  const [materialgroups, setMaterialgroups] = useState([]);
  const [attributeDetails, setAttributesDetails] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const navigate = useNavigate();

  const parseLocalList = (value) => {
    if (!value) {
      return [];
    }
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  // Static mapping of plant locations to their IDs
  const getPlantIdByLocation = (location) => {
    const plantMapping = {
      "RICH MORRISTOWN 1": "2515",
      "RICH MORRISTOWN 2": "2525",
    };
    return plantMapping[location] || "";
  };

  const getPlantOptions = () => {
    const fullLocations = parseLocalList(
      localStorage.getItem("MRO_user_full_location"),
    );
    const currentLocation = localStorage.getItem("MRO_user_location");

    // If only one plant or less, no options to switch
    if (fullLocations.length <= 1) {
      return [];
    }

    // Return all plants except the currently selected one
    return fullLocations
      .filter((location) => location !== currentLocation)
      .map((location) => ({
        location,
        plantId: getPlantIdByLocation(location),
      }));
  };

  const plantOptions = getPlantOptions();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLogout = () => {
    localStorage.removeItem("session_id");
    localStorage.removeItem("MRO_user_name");
    localStorage.removeItem("MRO_user_type");
    localStorage.removeItem("MRO_user_location");
    localStorage.removeItem("MRO_user_full_location");
    localStorage.removeItem("MRO_user_plantId");
    localStorage.removeItem("userLoginInfo");
    localStorage.removeItem("MRO_user_isSignedIn");
    instance.logout();
    setAnchorEl(null);
    navigate("/");
  };

  const handlePlantSwitch = (selectedPlant) => {
    // Update location to the selected plant
    localStorage.setItem("MRO_user_location", selectedPlant.location);

    // Update plantId based on the selected location
    const plantId = getPlantIdByLocation(selectedPlant.location);
    localStorage.setItem("MRO_user_plantId", plantId);

    localStorage.removeItem("description");
    localStorage.removeItem("manufacturing_part_number");
    localStorage.removeItem("plant_name");
    localStorage.removeItem("material_number");
    localStorage.removeItem("storage_location");
    localStorage.removeItem("storage_bin");

    setAnchorEl(null);
    navigate("/dashboard");
    window.location.reload();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoClick = () => {
    navigate("/dashboard");
  };

  const handleButtonClick = async () => {
    setCheckingRework(true);
    const userId =
      localStorage.getItem("MRO_user_email") ||
      localStorage.getItem("MRO_user_name") ||
      "";

    try {
      const response = await automationHttpRequest.post(
        "/rework_requests/",
        {
          user_id: userId,
        },
        {
          headers: {
            session_id: localStorage.getItem("session_id"),
            userEmail: localStorage.getItem("MRO_user_email"),
          },
          params: {},
        },
      );

      const responseData = response?.data?.data;
      const pendingRequests = Array.isArray(responseData)
        ? responseData
        : Array.isArray(response?.data)
          ? response?.data
          : [];

      if (pendingRequests.length > 0) {
        setReworkRequests(pendingRequests);
        setReworkDialogOpen(true);
      } else {
        navigate("/partcreation");
      }
    } catch (error) {
      toast.error("Unable to fetch rework requests. Please try again!", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 800,
      });
    } finally {
      setCheckingRework(false);
    }
  };

  const handleReworkDialogClose = () => {
    setReworkDialogOpen(false);
  };

  const handleContinueToCreatePart = () => {
    setReworkDialogOpen(false);
    navigate("/partcreation");
  };

  const fetchMaterialGroup = async () => {
    const response = await automationHttpRequest.get("/get_materialgroup/", {
      headers: {
        session_id: localStorage.getItem("session_id"),
        userEmail: localStorage.getItem("MRO_user_email"),
      },
    });

    if (response?.data?.status) {
      setMaterialgroups(response?.data?.data || []);
    }
  };

  const handleOpenReworkRequest = async (requestId, rowData) => {
    if (!requestId) return;

    const requestType =
      rowData?.request_type || rowData?.requestType || "Creation";

    if (requestType !== "Creation") {
      toast.error("Only Creation requests can be resubmitted from here.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 800,
      });
      return;
    }

    setPartRequestNumber(requestId);
    setPartCreationDialogOpen(true);
    setDialogLoading(true);
    setReworkDialogOpen(false);

    try {
      const response = await automationHttpRequest.post(
        "/get_Data_Owner_History_For_Creation/",
        { request_id: requestId.toString() },
        {
          headers: {
            session_id: localStorage.getItem("session_id"),
            userEmail: localStorage.getItem("MRO_user_email"),
          },
          params: {},
        },
      );

      if (response?.data?.status) {
        setPartCreationDetails(response?.data?.data?.[0]);
        setAttributesDetails(response?.data?.all_attribute_data || []);
        await fetchMaterialGroup();
      } else {
        setPartCreationDialogOpen(false);
        toast.error("Unable to open request details. Please try again!", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 800,
        });
      }
    } catch (error) {
      setPartCreationDialogOpen(false);
      toast.error("Unable to open request details. Please try again!", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 800,
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCreationDialogClose = () => {
    setPartCreationDialogOpen(false);
    setPartCreationDetails(undefined);
    setPartRequestNumber(undefined);
  };

  const handleRequestStatusUpdate = (requestId) => {
    setReworkRequests((prev) =>
      prev.filter((row) => {
        const rowRequestId = row?.request_id || row?.requestId;
        return String(rowRequestId) !== String(requestId);
      }),
    );
  };

  const location = useLocation();
  let currentPage = location.pathname;
  currentPage = currentPage.charAt(0).toUpperCase() + currentPage.slice(1);
  if (localStorage.getItem("session_id") === "" || undefined) {
    handleLogout();
  }

  const getEmailSpecificURL = (userType) => {
    if (
      userType === "edp-mro-llm-app-data-owner" ||
      userType === "edp-mro-llm-app-admin"
    ) {
      return "/partrequesthistory";
    } else if (
      userType === "edp-mro-llm-app-plant-owner" ||
      userType === "edp-mro-llm-app-maintenance-manager"
    ) {
      return "/parthistoryrequest";
    } else if (userType === "edp-mro-llm-app-etc") {
      return "/atcpartrequesthistory";
    }

    return "/parthistoryrequest";
  };

  const typoStyle = {
    color: "#000000",
    fontFamily: "Verdana",
    fontSize: "10px",
    fontWeight: "400",
    marginLeft: "20px",
    position: "relative",
    top: "-5px",
  };

  const nameStyle = {
    color: "#000000",
    fontFamily: "Verdana",
    fontSize: "18px",
    fontWeight: "400",
    marginLeft: "20px",
    position: "relative",
    top: "-5px",
  };

  const handleCreateNewUser = () => {
    // Replace "https://example.com" with the actual URL you want to open
    const url = "https://myaccount.microsoft.com/groups/groups-i-own";

    // Open the link in a new tab
    window.open(url, "_blank");
    // Optionally, you can also update the current tab's location
    // window.location.href = url;
  };

  return (
    <div className="header-container">
      <div className="logo-container">
        <img src={logo} alt="Logo" className="logo" onClick={handleLogoClick} />
      </div>

      <div className="page-title">
        <div className="header_tabs">
          <div
            className={`header-tab ${
              currentPage.startsWith("/dashboard") ? "active" : ""
            }`}
            onClick={() => navigate("/dashboard")}
          >
            {/* <HomeIcon className="icon" /> */}
            <Home
              className="icon"
              style={{
                position: "relative",
                left: "10px",
                bottom: "4px",
              }}
            />
            <Typography className="icon-text">Material Search</Typography>
          </div>
          {localStorage.getItem("MRO_user_type") !==
            "edp-mro-llm-app-search-limited" && (
            <div
              className={`header-tab ${
                currentPage.includes("history") ? "active" : ""
              }`}
              onClick={() =>
                navigate(
                  getEmailSpecificURL(localStorage.getItem("MRO_user_type")),
                )
              }
            >
              <PartRequest className="icon-partRequest" />
              <Typography className="icon-text">
                Part Request History
              </Typography>
            </div>
          )}
        </div>
      </div>

      <div className="icons-container">
        {(localStorage.getItem("MRO_user_type") ===
          "edp-mro-llm-app-plant-owner" ||
          localStorage.getItem("MRO_user_type") ===
            "edp-mro-llm-app-maintenance-manager" ||
          localStorage.getItem("MRO_user_type") ===
            "edp-mro-llm-app-admin") && (
          <button
            className="custom-button"
            onClick={handleButtonClick}
            disabled={checkingRework}
          >
            <AddIcon style={{ color: "#c51c1c", marginRight: "5px" }} />
            <span
              style={{
                color: "#c51c1c",
                position: "relative",
                top: "-6px",
                fontFamily: "Verdana",
                fontSize: "15px",
              }}
            >
              {checkingRework ? "Checking..." : "Create New Part"}
            </span>
          </button>
        )}

        {localStorage.getItem("MRO_user_type") === "edp-mro-llm-app-admin" && (
          <a
            href="#"
            className="custom-button"
            style={{ textDecoration: "none", display: "inline-block" }}
            onClick={(e) => {
              e.preventDefault();
              handleCreateNewUser();
            }}
          >
            <PersonAddRoundedIcon
              style={{ color: "#c51c1c", marginRight: "5px" }}
            />
            <span
              style={{
                color: "#c51c1c",
                position: "relative",
                top: "-6px",
                fontFamily: "Verdana",
                fontSize: "15px",
              }}
            >
              Manage Users
            </span>
          </a>
        )}

        <div className="userHeading">
          <UserIcon className="user-icon" />
          <div className="userSubHeading">
            <Typography className="icon" component="span" sx={typoStyle}>
              Welcome back,
            </Typography>

            <Typography component="span" className="icon" sx={nameStyle}>
              {localStorage.getItem("MRO_user_name")}
            </Typography>
            {localStorage.getItem("MRO_user_type") ===
            "edp-mro-llm-app-plant-owner" ? (
              <Typography sx={typoStyle}>Plant Owner</Typography>
            ) : localStorage.getItem("MRO_user_type") ===
              "edp-mro-llm-app-maintenance-manager" ? (
              <Typography sx={typoStyle}>Maintenance Manager</Typography>
            ) : localStorage.getItem("MRO_user_type") ===
              "edp-mro-llm-app-admin" ? (
              <Typography sx={typoStyle}>Admin</Typography>
            ) : localStorage.getItem("MRO_user_type") ===
              "edp-mro-llm-app-data-owner" ? (
              <Typography sx={typoStyle}>Data Owner</Typography>
            ) : localStorage.getItem("MRO_user_type") ===
              "edp-mro-llm-app-etc" ? (
              <Typography sx={typoStyle}>ETC</Typography>
            ) : (
              <Typography sx={typoStyle}></Typography>
            )}
            <Typography sx={typoStyle}>
              {localStorage.getItem("MRO_user_location")}
            </Typography>
          </div>
          <IconButton
            onClick={handleClick}
            color="inherit"
            aria-controls="user-menu"
            aria-haspopup="true"
            size="small"
            className="arrow-icon"
          >
            <ArrowIcon />
          </IconButton>
          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {plantOptions.map((plant) => (
              <MenuItem
                key={`${plant.location}-${plant.plantId}`}
                onClick={() => handlePlantSwitch(plant)}
                sx={{ px: "8px", py: "0px" }}
              >
                <Typography
                  sx={{
                    fontFamily: "Verdana",
                    fontSize: "16px",
                    fontWeight: "400",
                  }}
                >
                  Switch to {plant.location}
                </Typography>
              </MenuItem>
            ))}
            <MenuItem onClick={handleLogout} sx={{ px: "8px", py: "0px" }}>
              <Typography
                sx={{
                  fontFamily: "Verdana",
                  fontSize: "16px",
                  fontWeight: "400",
                }}
              >
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </div>
      </div>

      <ReworkRequestsDialog
        open={reworkDialogOpen}
        reworkRequests={reworkRequests}
        onClose={handleReworkDialogClose}
        onContinue={handleContinueToCreatePart}
        onRequestClick={handleOpenReworkRequest}
      />

      <OwnerPartCreationDialog
        open={partCreationDialogOpen}
        dialogData={partCreationDetails}
        materialgroups={materialgroups}
        attributeDetails={attributeDetails}
        closeDialog={handleCreationDialogClose}
        partRequestNumber={partRequestNumber}
        dialogLoading={dialogLoading}
        fetchPartRequestHistory={() => {}}
        onRequestStatusUpdate={handleRequestStatusUpdate}
        showSubmitOnly={true}
      />
    </div>
  );
};

export default Header;
